import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  useTheme,
  alpha
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
  Inbox as InboxIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  UnfoldMore as UnfoldMoreIcon,
  CallReceived as IncomingIcon,
  CallMade as OutgoingIcon,
  Assignment as ReturnIcon,
  SwapHoriz as MoveIcon
} from '@mui/icons-material';
import { UserMarkingCodeItem, SortField, Order } from '../types';
import { USER_STATUS_COLORS } from '../constants';

interface UserMarkingCodesTableProps {
  items: UserMarkingCodeItem[];
  totalCount: number;
  isLoading: boolean;
  page: number;
  pageSize: number;
  orderBy: SortField;
  order: Order;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newPageSize: number) => void;
  onSort: (field: SortField) => void;
  onOpenDocument?: (docNumber: string, stockIdIn?: string, stockIdOut?: string) => void;
}

// Заголовочная ячейка таблицы с поддержкой сортировки
const HeaderCell: React.FC<{
  children: React.ReactNode;
  sortField?: SortField;
  orderBy: SortField;
  order: Order;
  onSort?: (field: SortField) => void;
}> = ({ children, sortField, orderBy, order, onSort }) => {
  const isActive = sortField && orderBy === sortField;
  return (
    <Box
      onClick={sortField && onSort ? () => onSort(sortField) : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: sortField ? 'pointer' : 'default',
        userSelect: 'none',
        '&:hover': sortField ? { color: 'primary.main' } : {}
      }}
    >
      <Typography
        variant="subtitle2"
        color={isActive ? 'primary.main' : 'text.secondary'}
        fontWeight={700}
        sx={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}
      >
        {children}
      </Typography>
      {sortField && (isActive
        ? (order === 'asc' ? <ArrowUpIcon sx={{ fontSize: 16, color: 'primary.main' }} /> : <ArrowDownIcon sx={{ fontSize: 16, color: 'primary.main' }} />)
        : <UnfoldMoreIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
      )}
    </Box>
  );
};

export const UserMarkingCodesTable: React.FC<UserMarkingCodesTableProps> = ({
  items,
  totalCount,
  isLoading,
  page,
  pageSize,
  orderBy,
  order,
  onPageChange,
  onPageSizeChange,
  onSort,
  onOpenDocument
}) => {
  const theme = useTheme();
  const [copiedCode, setCopiedCode] = React.useState<string | null>(null);

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const getStatusChip = (item: UserMarkingCodeItem) => {
    const statusLabel = item.status || 'Не указан';
    const colorKey = (item.statusZid !== undefined && USER_STATUS_COLORS[item.statusZid])
      ? USER_STATUS_COLORS[item.statusZid]
      : USER_STATUS_COLORS[item.status] || 'default';

    let colorHex = theme.palette.text.secondary;
    if (colorKey === 'success') colorHex = theme.palette.success.main;
    else if (colorKey === 'info') colorHex = theme.palette.info.main;
    else if (colorKey === 'warning') colorHex = theme.palette.warning.main;
    else if (colorKey === 'primary') colorHex = theme.palette.primary.main;
    else if (colorKey === 'error') colorHex = theme.palette.error.main;

    return (
      <Chip
        label={statusLabel}
        size="small"
        sx={{
          height: 24,
          fontSize: '0.75rem',
          fontWeight: 500,
          bgcolor: alpha(colorHex, 0.1),
          color: colorHex,
          border: '1px solid',
          borderColor: alpha(colorHex, 0.2)
        }}
      />
    );
  };

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        width: '100%',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <TableContainer sx={{ minHeight: 380 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {/* Тип и направление */}
              <TableCell sx={{ py: 1.5, width: '18%' }}>
                <HeaderCell sortField="type" orderBy={orderBy} order={order} onSort={onSort}>
                  Тип / Направление
                </HeaderCell>
              </TableCell>

              {/* Статус */}
              <TableCell sx={{ py: 1.5, width: '13%' }}>
                <HeaderCell sortField="status" orderBy={orderBy} order={order} onSort={onSort}>
                  Статус
                </HeaderCell>
              </TableCell>

              {/* № документа */}
              <TableCell sx={{ py: 1.5, width: '13%' }}>
                <HeaderCell sortField="documentNumber" orderBy={orderBy} order={order} onSort={onSort}>
                  № документа
                </HeaderCell>
              </TableCell>

              {/* Товар и контрагент */}
              <TableCell sx={{ py: 1.5, width: '22%' }}>
                <HeaderCell sortField="materialName" orderBy={orderBy} order={order} onSort={onSort}>
                  Товар / Маршрут
                </HeaderCell>
              </TableCell>

              {/* Код маркировки */}
              <TableCell sx={{ py: 1.5, width: '22%' }}>
                <HeaderCell sortField="code" orderBy={orderBy} order={order} onSort={onSort}>
                  Код маркировки
                </HeaderCell>
              </TableCell>

              {/* Дата */}
              <TableCell sx={{ py: 1.5, width: '12%' }}>
                <HeaderCell sortField="date" orderBy={orderBy} order={order} onSort={onSort}>
                  Дата
                </HeaderCell>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              Array.from(new Array(6)).map((_, idx) => (
                <TableRow key={idx}>
                  <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={80} height={24} /></TableCell>
                  <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="90%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="90%" /></TableCell>
                  <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                </TableRow>
              ))
            ) : items.length > 0 ? (
              items.map((item) => {
                const isIncoming = item.isIncoming;
                const isReturn = item.typeZid === 4;

                return (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{
                      height: 52,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.04)
                      }
                    }}
                  >
                    {/* Тип и направление с фирменной плашкой как в DocumentList */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Tooltip title={
                          isReturn
                            ? 'Возврат'
                            : isIncoming
                              ? 'Входящее перемещение'
                              : 'Исходящее перемещение'
                        }>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 32,
                              height: 32,
                              borderRadius: '8px',
                              flexShrink: 0,
                              bgcolor: isReturn
                                ? alpha(theme.palette.info.main, 0.1)
                                : isIncoming
                                  ? alpha(theme.palette.success.main, 0.1)
                                  : alpha(theme.palette.primary.main, 0.1),
                              color: isReturn
                                ? theme.palette.info.main
                                : isIncoming
                                  ? theme.palette.success.main
                                  : theme.palette.primary.main
                            }}
                          >
                            {isReturn ? (
                              <ReturnIcon sx={{ fontSize: 18 }} />
                            ) : isIncoming ? (
                              <IncomingIcon sx={{ fontSize: 18 }} />
                            ) : (
                              <OutgoingIcon sx={{ fontSize: 18 }} />
                            )}
                          </Box>
                        </Tooltip>

                        <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {item.type || (isReturn ? 'Возврат' : 'Перемещение')}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.72rem',
                              fontWeight: 600,
                              color: isIncoming ? 'success.dark' : 'text.secondary'
                            }}
                          >
                            {isIncoming ? 'Входящее' : 'Исходящее'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Статус */}
                    <TableCell>{getStatusChip(item)}</TableCell>

                    {/* Номер документа - кликабельный */}
                    <TableCell>
                      <Tooltip title="Нажмите, чтобы просмотреть документ" arrow>
                        <Box
                          component="span"
                          onClick={() => {
                            if (onOpenDocument && item.documentNumber) {
                              onOpenDocument(item.documentNumber, item.stockIdIn, item.stockIdOut);
                            }
                          }}
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.85rem',
                            fontWeight: 700,
                            color: 'primary.main',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            py: 0.25,
                            px: 0.5,
                            borderRadius: 1,
                            transition: 'all 0.15s ease-in-out',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          #{item.documentNumber || '-'}
                        </Box>
                      </Tooltip>
                    </TableCell>

                    {/* Товар и контрагент */}
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: 280 }}>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          noWrap
                          title={item.materialName}
                          sx={{ color: 'text.primary' }}
                        >
                          {item.materialName || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {isIncoming ? (
                            <span>От: <strong>{item.stockNameOut || 'Не указан'}</strong></span>
                          ) : (
                            <span>Куда: <strong>{item.stockNameIn || 'Не указан'}</strong></span>
                          )}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Код маркировки */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                            color: 'text.primary',
                            wordBreak: 'break-all'
                          }}
                        >
                          {item.code}
                        </Typography>
                        <Tooltip title={copiedCode === item.code ? 'Скопировано!' : 'Скопировать код'}>
                          <IconButton size="small" onClick={() => handleCopy(item.code)} sx={{ p: 0.5 }}>
                            {copiedCode === item.code ? (
                              <CheckIcon fontSize="small" color="success" />
                            ) : (
                              <ContentCopyIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>

                    {/* Дата */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        {item.date || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} sx={{ py: 6, textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <InboxIcon sx={{ fontSize: 44, color: 'text.disabled' }} />
                    <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                      Данные не найдены
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
                      По выбранным фильтрам и поисковым критериям коды маркировки отсутствуют
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Пагинация */}
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => onPageChange(newPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => {
          onPageSizeChange(parseInt(e.target.value, 10));
          onPageChange(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Строк на странице:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}–${to} из ${count !== -1 ? count : `более чем ${to}`}`
        }
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider'
        }}
      />
    </Paper>
  );
};
